/**
 * ExecutionHub — Install execution hub.
 * URL: /execution?tab=schedule&sub=readiness
 *
 * Top tabs:    Schedule | Execute | Closeout
 * Sub-pivot (Schedule only): Readiness | Plan | Dispatch
 */
import React from "react";
import { useSearchParams } from "react-router-dom";
import { ShieldCheck, Map, Truck, CalendarDays, Zap, ClipboardCheck } from "lucide-react";
import { CockpitPage } from "@/components/cockpit/CockpitPage";
import { cn } from "@/lib/utils";
import { useRole } from "@/data/RoleContext";
import InstallReadinessCockpit from "@/pages/InstallReadinessCockpit";
import DispatchIntelligenceCockpit from "@/pages/DispatchIntelligenceCockpit";
import ProjectCloseoutCockpit from "@/pages/ProjectCloseoutCockpit";
import { PlanCockpit } from "@/components/scheduling/PlanCockpit";
import { PreInstallConfirmationQueue } from "@/components/scheduling/PreInstallConfirmationQueue";
import { NetworkExecTab } from "@/components/networkExec/NetworkExecTab";

type TopTab = "schedule" | "execute" | "closeout";
type SubPivot = "readiness" | "plan" | "dispatch";

const TOP_TABS: { id: TopTab; label: string; icon: React.ElementType }[] = [
  { id: "schedule", label: "Schedule", icon: CalendarDays },
  { id: "execute",  label: "Execute",  icon: Zap },
  { id: "closeout", label: "Closeout", icon: ClipboardCheck },
];

const SCHEDULE_SUBS: { id: SubPivot; label: string; icon: React.ElementType }[] = [
  { id: "readiness", label: "Readiness", icon: ShieldCheck },
  { id: "plan",      label: "Plan",      icon: Map },
  { id: "dispatch",  label: "Dispatch",  icon: Truck },
];

export default function ExecutionHub() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { role } = useRole();

  const activeTab = (searchParams.get("tab") ?? "schedule") as TopTab;
  const activeSub = (searchParams.get("sub") ?? "readiness") as SubPivot;

  function setTab(tab: TopTab) {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    if (tab === "schedule") {
      next.set("sub", activeSub);
    } else {
      next.delete("sub");
    }
    setSearchParams(next, { replace: true });
  }

  function setSub(sub: SubPivot) {
    const next = new URLSearchParams(searchParams);
    next.set("sub", sub);
    setSearchParams(next, { replace: true });
  }

  const activeSubLabel = SCHEDULE_SUBS.find(s => s.id === activeSub)?.label ?? "";
  const activeTabLabel = TOP_TABS.find(t => t.id === activeTab)?.label ?? "";
  const titleSuffix = activeTab === "schedule" ? activeSubLabel : activeTabLabel;

  return (
    <CockpitPage
      title="Install"
      titleSuffix={titleSuffix}
      maxWidth="7xl"
      tabs={
        <div className="flex items-center gap-x-6 border-b border-border/60">
          {TOP_TABS.map((t) => {
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={cn(
                  "relative flex items-center gap-1.5 pb-3 text-sm transition-colors",
                  active ? "text-foreground font-medium" : "text-muted-foreground hover:text-foreground"
                )}
              >
                <t.icon className="h-3.5 w-3.5 shrink-0" />
                {t.label}
                {active && <span className="absolute -bottom-px left-0 right-0 h-px bg-foreground" />}
              </button>
            );
          })}
        </div>
      }
      subPivot={
        activeTab === "schedule" ? (
          <div className="inline-flex items-center gap-1 p-0.5 rounded-lg bg-muted/60 border border-border/50">
            {SCHEDULE_SUBS.map(s => {
              const active = activeSub === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => setSub(s.id)}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all",
                    active
                      ? "bg-background text-foreground shadow-sm border border-border/60"
                      : "text-muted-foreground hover:text-foreground hover:bg-background/50"
                  )}
                >
                  <s.icon className="h-3.5 w-3.5 shrink-0" />
                  {s.label}
                </button>
              );
            })}
          </div>
        ) : undefined
      }
    >
      {activeTab === "schedule" && activeSub === "readiness" && (
        <InstallReadinessCockpit embedded />
      )}
      {activeTab === "schedule" && activeSub === "plan" && (
        <div className="space-y-4">
          <PlanCockpit />
          <PreInstallConfirmationQueue />
        </div>
      )}
      {activeTab === "schedule" && activeSub === "dispatch" && (
        <DispatchIntelligenceCockpit embedded />
      )}
      {activeTab === "execute" && (
        <NetworkExecTab
          isSuperAdmin={role === "super_admin"}
          actorId={role}
          actorRole={role}
        />
      )}
      {activeTab === "closeout" && (
        <ProjectCloseoutCockpit />
      )}
    </CockpitPage>
  );
}
